import ApiResponse from "@/models/ApiResponse";
import http, { checkResponse, handleApiResponse } from "@/services/http";
import Glim from "../models/generalLedgerInvoiceMirror";
import {
  DetailedType,
  DimActivity,
  DimCodeType,
  DimSort,
  MainType,
} from "../models/TypesForEdit";
import { Period } from "../models/PeriodModell";
import { StatusChanged } from "../models/StatusChange";
import { Year } from "../models/YearModell";
import { Dimension } from "../models/Dimension";
import {
  DimensionListReportFilter,
  DimensionStatusOption,
} from "../models/DimensionListReportFilter";
import { GeneralLedgerStatementFilter } from "../models/GeneralLedgerStatement";
import {
  RevaluationItem,
  BookRevaluation,
} from "../models/Revaluation";
import { BookOpenClosePayload, BookOpenCloseResult, OpenCloseAccount, OpenCloseAccountDetail } from "../models/OpenClose";
import { DimensionTransferPayload, DimensionTransferResult } from "../models/DimensionTransfer";
import {
  ModifyTransactionAccountsPayload,
  ModifyTransactionAccountsResult,
  CanModifyAccountsResult,
} from "../models/ModifyTransactionAccounts";
import {
  MonthlyExtract,
  MonthlyExtractDefaults,
  ReopenMonth,
  GenerateMonthlyExtractPayload,
  GenerateMonthlyExtractResult,
  DeleteMonthlyExtractResult,
} from "../models/MonthlyExtract";
import {
  RiportType,
  RiportRowType,
  RiportHead,
  RiportMainGroup,
  RiportGroup,
  RiportRow,
  RiportRowItem,
  RiportMainItem,
} from "../models/RiportDefinition";
import {
  ReportHeadOption,
  ReportTreeNode,
  RunReportParams,
  RunReportResult,
  ReportResultRow,
  ReportAccountRow,
  ReportAccountDetailRequest,
  ReportRunRowUpdate,
  ReportRunExportRequest,
  ReportAccountCheckRequest,
  ReportAccountCheckRow,
} from "../models/ReportRun";
import {
  BookingEventsLookups,
  BookingEventsRequest,
  BookingEventsPdfResult,
  BookingEventsTransactionLookup,
} from "../models/BookingEvents";
import {
  ParamReportFilter,
  ParamReportLookups,
  ParamReportPageResult,
  ParamReportPdfResult,
  ParamReportTransaction,
} from "../models/ParamReport";
import {
  RunCostMatrixParams,
  RunCostMatrixResult,
  CostMatrixResult,
} from "../models/CostMatrix";
import AccountNumberTreeNode from "../models/invoiceMirrorTreeNode";
import {
  BookingTemplate,
  BookingTemplateActionResult,
  BookingTemplateListItem,
  LoadBookingTemplateParams,
  SaveAsBookingTemplateParams,
} from "../models/BookingTemplate";
import { SelectChange } from "../models/TypesForEdit";
import { TransItemDimension } from "../models/generalLedgerTransactionItem";
import {
  BookTransactionImportParams,
  DeleteTransactionImportParams,
  SaveTransactionImportParams,
  TransactionImportBookResult,
  TransactionImportRow,
  TransactionImportUploadResult,
} from "../models/TransactionImport";
import { PayrollImportResult } from "../models/PayrollImport";
import {
  LedgerExportLookup,
  LedgerExportRequest,
  LedgerExportResult,
} from "../models/LedgerExport";
import {
  NovitaxImportResult,
  NovitaxPeriodCheck,
} from "../models/NovitaxImport";
import {
  DimensionRebookingRow,
  DimensionRebookingUploadResult,
  DimensionRebookingBookResult,
} from "../models/DimensionRebooking";

export default class GeneralLedgerInvoiceMirrorApi {

  static async GetGeneralLedgerInvoiceMirrors(): Promise<Glim[]> {
    return http
      .get<ApiResponse<Glim[]>>(`generalledger/invoicemirror/0`)
      .then(handleApiResponse);
  }

  // Egy könyvelési év aktív főkönyvi számai (mmbokaccno) – az átértékelési számla választóhoz.
  // forAccnoMod=true: a "Főkönyvi szám módosítás" szűkebb listája (csak könyvelésre nyitott, kézi,
  // nem fixált kontírszám – K2D w_popup_accno:555). Enélkül a hívás változatlan.
  static async GetInvoiceMirrorsByYear(year: number, forAccnoMod = false): Promise<any[]> {
    const query = forAccnoMod ? "?forAccnoMod=true" : "";
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/invoice-mirror/${year}${query}`)
      .then(handleApiResponse);
  }

  // Egy könyvelési év dimenzió-kódjai (mmbokdimcode) – a sablon-tétel dim1-3 választóihoz (type 2/3/4).
  static async GetDimensionCodesByYear(year: number): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/dimension/${year}`)
      .then(handleApiResponse);
  }

  // Naplók (csbokbooktype) – a sablon-tétel napló választójához.
  static async GetBookTypes(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/book-types`)
      .then(handleApiResponse);
  }

  static async GetDimensions(itemId: number): Promise<TransItemDimension[]> {
    return await http
      .get<ApiResponse<TransItemDimension[]>>(`general-ledger-transaction/${itemId}/dimension`)
      .then(handleApiResponse)
  }

  static async UpdateGlim(invMirror: Glim): Promise<boolean> {
    return http
      .put<ApiResponse<boolean>>(
        `generalledger/invoicemirror/updateinvmirror`,
        invMirror
      )
      .then(checkResponse);
  }

  static async CreateInvMirror(invMirror: Glim): Promise<string> {
    return http
      .post<ApiResponse<string>>(
        `generalledger/invoicemirror/createinvmirror`,
        invMirror
      )
      .then(handleApiResponse);
  }

  static async GenerateDimensionsByYearId(yearId: number): Promise<boolean> {
    return http
      .post<ApiResponse<boolean>>(
        `generalledger/dimensioncode/generatedimensionsbyyearid/${yearId}`
      )
      .then(checkResponse);
  }

  static async DeleteDimensions(dimensionIds: number[]): Promise<boolean> {
    return http
      .post<ApiResponse<boolean>>(
        `generalledger/dimensioncode/deletedimensions`,
        dimensionIds
      )
      .then(checkResponse);
  }

  static async GetGeneralLedgerYears(): Promise<Year[]> {
    return http
      .get<ApiResponse<Year[]>>(`generalledger/yearperiod/years`)
      .then(handleApiResponse);
  }
  static async GetPeriods(yearId: number): Promise<Period[]> {
    return http
      .get<ApiResponse<Period[]>>(`generalledger/yearperiod/periods/${yearId}`)
      .then(handleApiResponse);
  }

  static async GetStatuses(): Promise<string[]> {
    return http
      .get<ApiResponse<string[]>>(`generalledger/yearperiod/statuses`)
      .then(handleApiResponse);
  }

  static async GetDimActivities(): Promise<DimActivity[]> {
    return http
      .get<ApiResponse<DimActivity[]>>(
        `generalledger/dimensioncode/dimactivities`
      )
      .then(handleApiResponse);
  }

  static async GetDimCodeTypes(): Promise<DimCodeType[]> {
    return http
      .get<ApiResponse<DimCodeType[]>>(
        `generalledger/dimensioncode/dimcodetypes`
      )
      .then(handleApiResponse);
  }

  static async SaveYear(year: Year) {
    return await http
      .post(`generalledger/yearperiod/saveyear`, year)
      .then(handleApiResponse);
  }

  static SaveStatus(statusChange: StatusChanged[]) {
    return http
      .put(`generalledger/yearperiod/savestatus`, statusChange)
      .then(handleApiResponse);
  }

  static GetInvMirrorTree(yearId: number): Promise<AccountNumberTreeNode[]> {
    return http
      .get<ApiResponse<AccountNumberTreeNode[]>>(
        `generalledger/invoicemirror/getinvoicemirrortree/${yearId}`
      )
      .then(handleApiResponse);
  }
  static async CreateInvMirrors(thisYear: number): Promise<string> {
    return await http
      .post<ApiResponse<string>>(
        `generalledger/invoicemirror/generateinvmirror/${thisYear}`
      )
      .then(handleApiResponse);
  }
  static async SetInvMirrorsSelected(
    allItemsSelected: boolean,
    yearId: number
  ): Promise<AccountNumberTreeNode[]> {
    const d: SelectChange = {
      allItemsSelected: allItemsSelected,
      yearId: yearId,
    };
    return http
      .post<ApiResponse<AccountNumberTreeNode[]>>(
        `generalledger/invoicemirror/setinvmirrortreeselect`,
        d
      )
      .then(handleApiResponse);
  }
  static CreateGeneratedGlims(generatedGlim: Glim[]) {
    return http
      .post(`generalledger/creategeneratedglims`, generatedGlim)
      .then(handleApiResponse);
  }

  static async GetMainTypes(): Promise<MainType[]> {
    return await http
      .get<ApiResponse<MainType[]>>(`generalledger/yearperiod/maintypes`)
      .then(handleApiResponse);
  }

  static async GetDetailedTypes(): Promise<DetailedType[]> {
    return await http
      .get<ApiResponse<DetailedType[]>>(
        `generalledger/yearperiod/detailedtypes`
      )
      .then(handleApiResponse);
  }
  static async GetDimSortTypes(): Promise<DimSort[]> {
    return await http
      .get<ApiResponse<DimSort[]>>(`generalledger/dimensioncode/dimsorttypes`)
      .then(handleApiResponse);
  }

  static async DeleteGlims(glims: number[]): Promise<boolean> {
    return await http
      .post(`generalledger/invoicemirror/deleteinvmirrors`, glims)
      .then(checkResponse);
  }
  static async SavePeriodsStatusChanges(periods: Period[]): Promise<boolean> {
    return await http
      .put(`generalledger/yearperiod/updateperiods`, periods)
      .then(checkResponse);
  }

  static async UpdateDim(dim: Dimension): Promise<boolean> {
    return await http
      .put<ApiResponse<boolean>>(`generalledger/dimensioncode/updatedim`, dim)
      .then(handleApiResponse);
  }
  static async UpdateDimStatuses(changedDimStatuses: Dimension[]) {
    return await http
      .put<ApiResponse<boolean>>(
        `generalledger/dimensioncode/updatedimstatus`,
        changedDimStatuses
      )
      .then(handleApiResponse);
  }
  static async UpdateGlimStatuses(glims: Glim[]) {
    return await http
      .put<ApiResponse<boolean>>(
        `generalledger/invoicemirror/updateinvmirrorstatuses`,
        glims
      )
      .then(checkResponse);
  }

  static getOpenTreeItems(yearId: number): Promise<number[]> {
    return http
      .get<ApiResponse<number[]>>(
        `generalledger/invoicemirror/getopentreeitems/${yearId}`
      )
      .then(handleApiResponse);
  }

  // Riportok -> Dimenzió lista (K2D w_popup_report_mirror 'D' mód portja)
  static async SearchDimensionListReport(
    filter: DimensionListReportFilter
  ): Promise<Dimension[]> {
    return http
      .post<ApiResponse<Dimension[]>>(
        `generalledger/dimension-list-report/search`,
        filter
      )
      .then(handleApiResponse);
  }

  static async GetDimensionListStatuses(): Promise<DimensionStatusOption[]> {
    return http
      .get<ApiResponse<DimensionStatusOption[]>>(
        `generalledger/dimension-list-report/statuses`
      )
      .then(handleApiResponse);
  }

  // Dimenzió lista "Generálás": a xd16bokdimcode.rpt Crystal riport PDF-je Base64-ben.
  static async GenerateDimensionListReportPdf(
    filter: DimensionListReportFilter
  ): Promise<string> {
    return http
      .post<ApiResponse<string>>(
        `generalledger/dimension-list-report/generate-pdf`,
        filter
      )
      .then(handleApiResponse);
  }

  // ── Riportok -> Kivonat (Főkönyvi kivonat, K2D w_popup_report_brief '-' mód) ──

  // A "Generálás" gomb: a xd16bokext1.rpt Crystal riport PDF-je Base64-ben.
  static async GenerateGeneralLedgerStatementPdf(
    filter: GeneralLedgerStatementFilter
  ): Promise<string> {
    return http
      .post<ApiResponse<string>>(
        `generalledger/general-ledger-statement/generate-pdf`,
        filter
      )
      .then(handleApiResponse);
  }

  // A "BI Riport" gomb: a PDF-fel közös adat-előkészítés PDF nélkül; a visszaadott hlboktmpext fej-id
  // megy a beágyazott Power BI riportnak szűrőként (ilboktmpext.head = id).
  static async PrepareGeneralLedgerStatementBiReport(
    filter: GeneralLedgerStatementFilter
  ): Promise<number> {
    return http
      .post<ApiResponse<number>>(
        `generalledger/general-ledger-statement/prepare-bi`,
        filter
      )
      .then(handleApiResponse);
  }

  // "Pontozás" választó-fül: az adott könyvelési év összepontozás (recon) fejei.
  static async GetStatementRecons(year: number): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`generalledger/general-ledger-statement/recon/${year}`)
      .then(handleApiResponse);
  }

  // "Fizetés módja" legördülő (karton) – a közös payment-mode végpont (K2D dddw_paymode megfelelője:
  // national felirat / szam érték; az SP-nek az 1 = "Mind").
  static async GetStatementPayModes(): Promise<any[]> {
    return http.get<ApiResponse<any[]>>(`payment-mode`).then(handleApiResponse);
  }

  // A választó-fülek év-független listái a meglévő tranzakciós/partner végpontokat használják.
  static async GetStatementPartners(): Promise<any[]> {
    return http.get<ApiResponse<any[]>>(`partners/min`).then(handleApiResponse);
  }

  static async GetStatementCars(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/cars`)
      .then(handleApiResponse);
  }

  static async GetStatementProjects(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/projects`)
      .then(handleApiResponse);
  }

  static async GetStatementPurchaseOrders(): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/pos`)
      .then(handleApiResponse);
  }

  // "Dim 1-3" választó-fül és a Dim I/II/III szűrők: az adott naptári év dimenziói
  // (AccountingDimCode numerikus type-pal – dim1=2, dim2=3, dim3=4).
  static async GetStatementDimensions(year: number): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/dimension/${year}`)
      .then(handleApiResponse);
  }

  // Akciók -> Elhatárolás átértékelés (K2D w_revalue portja)
  static async GetRevaluationItems(
    transId: number
  ): Promise<RevaluationItem[]> {
    return http
      .get<ApiResponse<RevaluationItem[]>>(
        `generalledger/revaluation/transactions/${transId}/items`
      )
      .then(handleApiResponse);
  }

  static async BookRevaluation(payload: BookRevaluation): Promise<number> {
    return http
      .post<ApiResponse<number>>(`generalledger/revaluation/book`, payload)
      .then(handleApiResponse);
  }

  // Akciók -> Nyitás-zárás: a mód szerinti alapértelmezett naplótípus id-ja (K2D d_f_zaras_fej book_type).
  static async GetOpenCloseDefaultBookType(mode: string): Promise<number> {
    return http
      .get<ApiResponse<number>>(`generalledger/openclose/default-book-type?mode=${mode}`)
      .then(handleApiResponse);
  }

  // Akciók -> Nyitás-zárás (K2D w_popup_zaras / dw_2): a kiválasztott FKsz részletezése partnerenként (Tartozik/Követel/Egyenleg).
  static async GetOpenCloseAccountDetail(
    yearId: number,
    accno: number,
    mode: string,
    isPartner = false
  ): Promise<OpenCloseAccountDetail[]> {
    return http
      .get<ApiResponse<OpenCloseAccountDetail[]>>(
        `generalledger/openclose/account-detail?yearId=${yearId}&accno=${accno}&mode=${mode}&isPartner=${isPartner}`
      )
      .then(handleApiResponse);
  }

  // Akciók -> Nyitás-zárás (K2D w_popup_zaras / dw_1): a módban (N/Z/A) nyitható/zárható/átvezethető FKsz-ok a cél számokkal.
  static async GetOpenCloseAccounts(
    yearId: number,
    mode: string,
    hideTransferred = false
  ): Promise<OpenCloseAccount[]> {
    return http
      .get<ApiResponse<OpenCloseAccount[]>>(
        `generalledger/openclose/accounts?yearId=${yearId}&mode=${mode}&hideTransferred=${hideTransferred}`
      )
      .then(handleApiResponse);
  }

  // Akciók -> Nyitás-zárás (K2D w_popup_zaras portja): nyitás/zárás/átvezetés könyvelése.
  static async BookOpenClose(payload: BookOpenClosePayload): Promise<BookOpenCloseResult> {
    return http
      .post<ApiResponse<BookOpenCloseResult>>(`generalledger/openclose/book`, payload)
      .then(handleApiResponse);
  }

  // Javítás -> Dimenzió átkönyvelés (K2D w_dim_mod portja): kontrolling átvezetés dimenzió-kódok között.
  static async PostDimensionTransfer(payload: DimensionTransferPayload): Promise<DimensionTransferResult> {
    return http
      .post<ApiResponse<DimensionTransferResult>>(`generalledger/dimension-transfer/book`, payload)
      .then(handleApiResponse);
  }

  // A költséghely-választó listája (K2D w_dim_mod-dal azonos szűrő: type=2 + adott év, status/gyűjtő szűrés nélkül).
  static async GetCostCenterDimcodes(year: number): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`generalledger/dimension-transfer/cost-centers/${year}`)
      .then(handleApiResponse);
  }

  // Javítás -> Főkönyvi számok mód.: indítható-e a művelet ezen a tranzakción (a szerkesztő gombjának kapuzása).
  static async CanModifyAccounts(transId: number): Promise<CanModifyAccountsResult> {
    return http
      .get<ApiResponse<CanModifyAccountsResult>>(`generalledger/modify-accounts/transactions/${transId}/can-modify`)
      .then(handleApiResponse);
  }

  // Javítás -> Főkönyvi számok mód.: a módosítás végrehajtása (javított másolat + opcionális sztornó).
  static async ModifyTransactionAccounts(
    payload: ModifyTransactionAccountsPayload
  ): Promise<ModifyTransactionAccountsResult> {
    return http
      .post<ApiResponse<ModifyTransactionAccountsResult>>(`generalledger/modify-accounts/modify`, payload)
      .then(handleApiResponse);
  }

  // Export és import -> Havi zárás: a képernyő alapértékei (következő zárandó hónap + zárás dátuma).
  static async GetMonthlyExtractDefaults(): Promise<MonthlyExtractDefaults> {
    return http
      .get<ApiResponse<MonthlyExtractDefaults>>(`generalledger/monthly-extract/defaults`)
      .then(handleApiResponse);
  }

  // Havi zárás: az adott hónaphoz az újranyitandó korábbi hónapok.
  static async GetReopenMonths(yearMonth: string, closeDate: string): Promise<ReopenMonth[]> {
    return http
      .get<ApiResponse<ReopenMonth[]>>(
        `generalledger/monthly-extract/reopen-months?yearMonth=${yearMonth}&closeDate=${closeDate}`
      )
      .then(handleApiResponse);
  }

  // Havi zárás (kivonat napló): a periódusok listája.
  static async GetMonthlyExtracts(): Promise<MonthlyExtract[]> {
    return http
      .get<ApiResponse<MonthlyExtract[]>>(`generalledger/monthly-extract/extracts`)
      .then(handleApiResponse);
  }

  // Havi zárás: a kivonat generálása (spp_valbok).
  static async GenerateMonthlyExtract(
    payload: GenerateMonthlyExtractPayload
  ): Promise<GenerateMonthlyExtractResult> {
    return http
      .post<ApiResponse<GenerateMonthlyExtractResult>>(`generalledger/monthly-extract/generate`, payload)
      .then(handleApiResponse);
  }

  // Havi zárás (kivonat napló): egy periódus logikai törlése.
  static async DeleteMonthlyExtract(id: number): Promise<DeleteMonthlyExtractResult> {
    return http
      .post<ApiResponse<DeleteMonthlyExtractResult>>(`generalledger/monthly-extract/${id}/delete`)
      .then(handleApiResponse);
  }

  // ── Törzskarbantartás -> Főkönyvi riportok (w_repheads) – definíció-szerkesztő ──

  static async GetRiportTypes(): Promise<RiportType[]> {
    return http.get<ApiResponse<RiportType[]>>(`generalledger-riport/types`).then(handleApiResponse);
  }
  static async GetRiportRowTypes(reportType: number): Promise<RiportRowType[]> {
    return http
      .get<ApiResponse<RiportRowType[]>>(`generalledger-riport/rowtypes/type/${reportType}`)
      .then(handleApiResponse);
  }
  static async GetRiportHeadsByType(reportType: number, includeDeleted = false): Promise<RiportHead[]> {
    return http
      .get<ApiResponse<RiportHead[]>>(`generalledger-riport/heads/type/${reportType}?includeDeleted=${includeDeleted}`)
      .then(handleApiResponse);
  }
  static async GetRiportMainGroups(headId: number, includeDeleted = false): Promise<RiportMainGroup[]> {
    return http
      .get<ApiResponse<RiportMainGroup[]>>(`generalledger-riport/heads/${headId}/maingroups?includeDeleted=${includeDeleted}`)
      .then(handleApiResponse);
  }
  static async GetRiportGroups(mainGroupId: number, includeDeleted = false): Promise<RiportGroup[]> {
    return http
      .get<ApiResponse<RiportGroup[]>>(`generalledger-riport/maingroups/${mainGroupId}/groups?includeDeleted=${includeDeleted}`)
      .then(handleApiResponse);
  }
  static async GetRiportRows(groupId: number, includeDeleted = false): Promise<RiportRow[]> {
    return http
      .get<ApiResponse<RiportRow[]>>(`generalledger-riport/groups/${groupId}/rows?includeDeleted=${includeDeleted}`)
      .then(handleApiResponse);
  }
  static async GetRiportRowItemsByRow(rowId: number, includeDeleted = false): Promise<RiportRowItem[]> {
    return http
      .get<ApiResponse<RiportRowItem[]>>(`generalledger-riport/rows/${rowId}/rowitems?includeDeleted=${includeDeleted}`)
      .then(handleApiResponse);
  }
  static async GetRiportMainItems(mainGroupId: number): Promise<RiportMainItem[]> {
    return http
      .get<ApiResponse<RiportMainItem[]>>(`generalledger-riport/maingroups/${mainGroupId}/items`)
      .then(handleApiResponse);
  }
  // Az "elem"-szerkesztő legördülőihez: a fej ÖSSZES csoportja / sora (a hivatkozható csomópontok).
  static async GetRiportGroupsByHead(headId: number): Promise<RiportGroup[]> {
    return http
      .get<ApiResponse<RiportGroup[]>>(`generalledger-riport/heads/${headId}/all-groups`)
      .then(handleApiResponse);
  }
  static async GetRiportRowsByHead(headId: number): Promise<RiportRow[]> {
    return http
      .get<ApiResponse<RiportRow[]>>(`generalledger-riport/heads/${headId}/all-rows`)
      .then(handleApiResponse);
  }
  // A sortétel dimenzió-maszkjaihoz: a riport évéhez tartozó dimenziók (a tranzakciós végpontot újrahasználva).
  static async GetReportDimensions(year: number): Promise<any[]> {
    return http
      .get<ApiResponse<any[]>>(`general-ledger-transaction/detail/dimension/${year}`)
      .then(handleApiResponse);
  }
  static async SaveRiportHead(data: RiportHead): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/head`, data).then(handleApiResponse);
  }
  static async SaveRiportMainGroup(data: RiportMainGroup): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/maingroup`, data).then(handleApiResponse);
  }
  static async SaveRiportGroup(data: RiportGroup): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/group`, data).then(handleApiResponse);
  }
  static async SaveRiportRow(data: RiportRow): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/row`, data).then(handleApiResponse);
  }
  static async SaveRiportRowItem(data: RiportRowItem): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/rowitem`, data).then(handleApiResponse);
  }
  static async SaveRiportMainItem(data: RiportMainItem): Promise<number> {
    return http.post<ApiResponse<number>>(`generalledger-riport/mainitem`, data).then(handleApiResponse);
  }
  // Fizikai törlés (a táblán nincs live_dead) – POST, mert a http kliensben nincs delete.
  static async DeleteRiportMainItem(id: number): Promise<boolean> {
    return http.post<ApiResponse<boolean>>(`generalledger-riport/mainitem/delete/${id}`, {}).then(handleApiResponse);
  }
  // A fel/le mozgatás (OrderNo) kötegelt, atomi mentése egy szintre (maingroup/group/row).
  static async ReorderRiportNodes(level: string, items: { id: number; orderNo: number }[]): Promise<boolean> {
    return http.post<ApiResponse<boolean>>(`generalledger-riport/reorder`, { level, items }).then(handleApiResponse);
  }

  // Riportok -> Mérleg / Eredménykimutatás (K2D w_popup_report_bokrep M/E mód portja)

  // Riport-definíció választólista (reptype: 2 = mérleg, 3 = eredménykimutatás).
  static async GetReportHeads(reportType: number): Promise<ReportHeadOption[]> {
    return http
      .get<ApiResponse<ReportHeadOption[]>>(`generalledger/report-run/reportheads/${reportType}`)
      .then(handleApiResponse);
  }

  // A riport-definíció fája (főcsoport -> csoport -> sor) a fksz-részletezés sor-kijelöléséhez.
  static async GetReportTree(reportHeadId: number): Promise<ReportTreeNode[]> {
    return http
      .get<ApiResponse<ReportTreeNode[]>>(`generalledger/report-run/tree/${reportHeadId}`)
      .then(handleApiResponse);
  }

  // Futtatás: hmboktemprep fej + kijelölt sorok + spp_boktemprep1.
  static async RunReport(params: RunReportParams): Promise<RunReportResult> {
    return http
      .post<ApiResponse<RunReportResult>>(`generalledger/report-run/run`, params)
      .then(handleApiResponse);
  }

  // A lefuttatott riport sorai (lang: 1=magyar, 2=angol, 3=idegen).
  static async GetReportRows(runId: number, mode: string, lang: number): Promise<ReportResultRow[]> {
    return http
      .get<ApiResponse<ReportResultRow[]>>(`generalledger/report-run/run/${runId}/rows/${mode}?lang=${lang}`)
      .then(handleApiResponse);
  }

  // Fksz. ellenőrzés: a riport maszkjaiba nem eső könyvelt tételek az időszakban.
  static async GetReportAccountCheck(payload: ReportAccountCheckRequest): Promise<ReportAccountCheckRow[]> {
    return http
      .post<ApiResponse<ReportAccountCheckRow[]>>(`generalledger/report-run/account-check`, payload)
      .then(handleApiResponse);
  }

  // Egy riport-sor főkönyviszám-szintű albontása.
  static async GetReportAccountDetail(payload: ReportAccountDetailRequest): Promise<ReportAccountRow[]> {
    return http
      .post<ApiResponse<ReportAccountRow[]>>(`generalledger/report-run/run/account-detail`, payload)
      .then(handleApiResponse);
  }

  // A képernyőn átírt sorok visszaírása (K2D pb_print eleje) — nyomtatás/export előtt.
  static async UpdateReportRows(rows: ReportRunRowUpdate[]): Promise<string> {
    return http
      .post<ApiResponse<string>>(`generalledger/report-run/run/rows/update`, { rows })
      .then(handleApiResponse);
  }

  // Crystal PDF (xd16boktemprep3.rpt) Base64-ben.
  static async GetReportPdf(payload: ReportRunExportRequest): Promise<string> {
    return http
      .post<ApiResponse<string>>(`generalledger/report-run/run/pdf`, payload)
      .then(handleApiResponse);
  }

  // Excel export (xlsx) Base64-ben.
  static async GetReportExcel(runId: number, mode: string, lang: number): Promise<string> {
    return http
      .get<ApiResponse<string>>(`generalledger/report-run/run/${runId}/excel/${mode}?lang=${lang}`)
      .then(handleApiResponse);
  }

  // Riportok -> Költségmátrix (K2D w_popup_report_bokrep 'X' mód portja)

  // Futtatás: hmboktemprep fej + spp_bokmatrix -> imbokmatrix.
  static async RunCostMatrix(params: RunCostMatrixParams): Promise<RunCostMatrixResult> {
    return http
      .post<ApiResponse<RunCostMatrixResult>>(`generalledger/report-run/cost-matrix/run`, params)
      .then(handleApiResponse);
  }

  // A lefuttatott mátrix pivot formában (oszlopok = I. dimenzió kódok).
  static async GetCostMatrixRows(runId: number): Promise<CostMatrixResult> {
    return http
      .get<ApiResponse<CostMatrixResult>>(`generalledger/report-run/cost-matrix/${runId}/rows`)
      .then(handleApiResponse);
  }

  // Excel export (xlsx) Base64-ben.
  static async GetCostMatrixExcel(runId: number): Promise<string> {
    return http
      .get<ApiResponse<string>>(`generalledger/report-run/cost-matrix/${runId}/excel`)
      .then(handleApiResponse);
  }

  // Crystal PDF (xd16bokmatrix.rpt) Base64-ben.
  static async GetCostMatrixPdf(runId: number): Promise<string> {
    return http
      .get<ApiResponse<string>>(`generalledger/report-run/cost-matrix/${runId}/pdf`)
      .then(handleApiResponse);
  }

  // Riportok -> Könyvelési események (K2D w_popup_report_konyv_esemeny portja)

  // Napló- és állapot-választólisták a szűrőpanelhez (K2D defaultokkal).
  static async GetBookingEventsLookups(): Promise<BookingEventsLookups> {
    return http
      .get<ApiResponse<BookingEventsLookups>>(`generalledger/booking-events/lookups`)
      .then(handleApiResponse);
  }

  // Tranzakció-választólista (szam + gazdasági esemény) a TR-intervallum lookuphoz.
  static async GetBookingEventsTransactions(): Promise<BookingEventsTransactionLookup[]> {
    return http
      .get<ApiResponse<BookingEventsTransactionLookup[]>>(`generalledger/booking-events/transactions`)
      .then(handleApiResponse);
  }

  // A könyvelési események Crystal riport (xd16bokaccterm) PDF-je Base64-ben.
  static async GetBookingEventsPdf(params: BookingEventsRequest): Promise<BookingEventsPdfResult> {
    return http
      .post<ApiResponse<BookingEventsPdfResult>>(`generalledger/booking-events/pdf`, params)
      .then(handleApiResponse);
  }

  // Riportok -> Paraméteres (K2D w_popup_report_param portja)

  // Napló-, prefix- és rögzítő-választólisták a szűrőpanelhez.
  static async GetParamReportLookups(): Promise<ParamReportLookups> {
    return http
      .get<ApiResponse<ParamReportLookups>>(`generalledger/param-report/lookups`)
      .then(handleApiResponse);
  }

  // A "Tranzakció (-tól/-ig)" választó listája (K2D w_popup_hxboktrans_query).
  static async GetParamReportTransactions(): Promise<ParamReportTransaction[]> {
    return http
      .get<ApiResponse<ParamReportTransaction[]>>(`generalledger/param-report/transactions`)
      .then(handleApiResponse);
  }

  // Az eredmény-lista lapozva (K2D "Eredmény megtekintése" fül).
  static async SearchParamReport(filter: ParamReportFilter, currentPage: number, pageSize: number): Promise<ParamReportPageResult> {
    return http
      .post<ApiResponse<ParamReportPageResult>>(`generalledger/param-report/search`, { filter, currentPage, pageSize })
      .then(handleApiResponse);
  }

  // Excel export (xlsx) Base64-ben a teljes eredményből.
  static async GetParamReportExcel(filter: ParamReportFilter): Promise<string> {
    return http
      .post<ApiResponse<string>>(`generalledger/param-report/excel`, filter)
      .then(handleApiResponse);
  }

  // A Crystal riport (xd16bokparam) PDF-je Base64-ben.
  static async GetParamReportPdf(filter: ParamReportFilter, printCoverPage: boolean): Promise<ParamReportPdfResult> {
    return http
      .post<ApiResponse<ParamReportPdfResult>>(`generalledger/param-report/pdf`, { filter, printCoverPage })
      .then(handleApiResponse);
  }

  // Törzs -> Könyvelési sablonok (K2D w_popupu_hmboktranstemp / w_resp_hmboktranstemp portja)

  // Élő sablonok listája.
  static async GetBookingTemplates(): Promise<BookingTemplateListItem[]> {
    return http
      .get<ApiResponse<BookingTemplateListItem[]>>(`generalledger/booking-template`)
      .then(handleApiResponse);
  }

  // Egy sablon fej + tételek + dimenzió-felosztások.
  static async GetBookingTemplate(id: number): Promise<BookingTemplate> {
    return http
      .get<ApiResponse<BookingTemplate>>(`generalledger/booking-template/${id}`)
      .then(handleApiResponse);
  }

  // Sablon létrehozás/módosítás (fej + tételek egyben).
  static async SaveBookingTemplate(data: BookingTemplate): Promise<BookingTemplateActionResult> {
    return http
      .post<ApiResponse<BookingTemplateActionResult>>(`generalledger/booking-template`, data)
      .then(handleApiResponse);
  }

  // Sablon fizikai törlése (K2D kuka gomb).
  static async DeleteBookingTemplate(id: number): Promise<BookingTemplateActionResult> {
    return http
      .delete<ApiResponse<BookingTemplateActionResult>>(`generalledger/booking-template/${id}`)
      .then(handleApiResponse);
  }

  // Sablon betöltése mentett, üres tranzakcióba (spp_trtemp_load).
  static async LoadBookingTemplate(params: LoadBookingTemplateParams): Promise<BookingTemplateActionResult> {
    return http
      .post<ApiResponse<BookingTemplateActionResult>>(`generalledger/booking-template/load`, params)
      .then(handleApiResponse);
  }

  // Tranzakció mentése sablonként (spp_trtemp_save).
  static async SaveTransactionAsTemplate(params: SaveAsBookingTemplateParams): Promise<BookingTemplateActionResult> {
    return http
      .post<ApiResponse<BookingTemplateActionResult>>(`generalledger/booking-template/save-as`, params)
      .then(handleApiResponse);
  }

  // Export/Import -> Tranzakció import (K2D w_popup_tmp_boktrans portja)

  // A staging (hmboktmptrans) teljes tartalma.
  static async GetTransactionImportRows(): Promise<TransactionImportRow[]> {
    return http
      .get<ApiResponse<TransactionImportRow[]>>(`generalledger/transaction-import`)
      .then(handleApiResponse);
  }

  // CSV/Excel fájl beparse-olása + validálása; a sorokat a kliens-puffernek adja vissza (nem ír DB-be).
  static async UploadTransactionImportFile(file: File): Promise<TransactionImportUploadResult> {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post<ApiResponse<TransactionImportUploadResult>>(`generalledger/transaction-import/upload`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // A kliens-puffer teljes tartalmának mentése a stagingbe (full-state sync: insert/update/delete).
  static async SaveTransactionImportRows(params: SaveTransactionImportParams): Promise<TransactionImportUploadResult> {
    return http
      .post<ApiResponse<TransactionImportUploadResult>>(`generalledger/transaction-import/save`, params)
      .then(handleApiResponse);
  }

  // Kijelölt sorok könyvelése (spp_boktmptrans) vagy checkOnly = a teljes staging validálása.
  static async BookTransactionImport(params: BookTransactionImportParams): Promise<TransactionImportBookResult> {
    return http
      .post<ApiResponse<TransactionImportBookResult>>(`generalledger/transaction-import/book`, params)
      .then(handleApiResponse);
  }

  // Staging sorok törlése (rowIds) vagy a teljes staging ürítése (all).
  static async DeleteTransactionImportRows(params: DeleteTransactionImportParams): Promise<TransactionImportUploadResult> {
    return http
      .post<ApiResponse<TransactionImportUploadResult>>(`generalledger/transaction-import/delete`, params)
      .then(handleApiResponse);
  }

  // Üres Excel import-sablon (base64 xlsx).
  static async GetTransactionImportTemplate(): Promise<string> {
    return http
      .get<ApiResponse<string>>(`generalledger/transaction-import/template`)
      .then(handleApiResponse);
  }

  // Export/Import -> Bérfeladás (K2D ue_dun_ber TXT / w_popup_import XLS portja)

  // Bérfeladás TXT fájlból (10 oszlopos, TAB-elválasztott).
  static async ImportPayrollTxt(file: File): Promise<PayrollImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post<ApiResponse<PayrollImportResult>>(`generalledger/payroll-import/txt`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // Bérfeladás Excel fájlból (élő "új" formátum, dolgozónév-alapú dimenzió-feloldással).
  static async ImportPayrollXls(file: File): Promise<PayrollImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post<ApiResponse<PayrollImportResult>>(`generalledger/payroll-import/xls`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // Javítás -> Átkönyvelés excelből (K2D m_bok_dim_xls / ue_bok_dim_xls portja)

  // Az átkönyvelési Excel beolvasása és sor-validálása (DB-írás nélkül).
  static async UploadDimensionRebookingFile(file: File): Promise<DimensionRebookingUploadResult> {
    const formData = new FormData();
    formData.append("file", file);
    return http
      .post<ApiResponse<DimensionRebookingUploadResult>>(`generalledger/dimension-rebooking/upload`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // A beolvasott sorok kötegelt átvezetése (soronként spp_bok_to_dim, all-or-nothing).
  static async BookDimensionRebooking(rows: DimensionRebookingRow[]): Promise<DimensionRebookingBookResult> {
    return http
      .post<ApiResponse<DimensionRebookingBookResult>>(`generalledger/dimension-rebooking/book`, rows)
      .then(handleApiResponse);
  }

  // Üres átkönyvelési Excel-sablon (K2D "Átkönyvelési sablon"), Base64 xlsx.
  static async GetDimensionRebookingTemplate(): Promise<string> {
    return http
      .get<ApiResponse<string>>(`generalledger/dimension-rebooking/template`)
      .then(handleApiResponse);
  }

  // Export/Import -> NOVITAX import (K2D m_novitax_csv / m_novitax_xls portja)

  // NOVITAX CSV beemelése (5 oszlopos, pontosvesszős; a dátum/esemény/státusz az űrlapról jön).
  static async ImportNovitaxCsv(file: File, accDate: string, accEvent: string, status: number,
    force: boolean): Promise<NovitaxImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("accDate", accDate);
    formData.append("accEvent", accEvent);
    formData.append("status", String(status));
    formData.append("force", String(force));
    return http
      .post<ApiResponse<NovitaxImportResult>>(`generalledger/novitax-import/csv`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // NOVITAX XLS beemelése (NAPLÓJEL formátum; bizonylatszámonként külön fej).
  static async ImportNovitaxXls(file: File, force: boolean): Promise<NovitaxImportResult> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("force", String(force));
    return http
      .post<ApiResponse<NovitaxImportResult>>(`generalledger/novitax-import/xls`, formData, {
        headers: { "Content-Type": "multipart/mixed" },
      })
      .then(handleApiResponse);
  }

  // A CSV űrlap dátum-ellenőrzése + gazdaságiesemény-javaslat (K2D itemchanged).
  static async CheckNovitaxPeriod(date: string): Promise<NovitaxPeriodCheck> {
    return http
      .get<ApiResponse<NovitaxPeriodCheck>>(`generalledger/novitax-import/period-check?date=${date}`)
      .then(handleApiResponse);
  }

  // Export/Import -> Főkönyvi export (K2D m_dun_exp / w_popup_datuminput portja)

  // A főkönyviszám-kijelölő lista (könyvelésre nyitott, nem gyűjtő számlák).
  static async GetLedgerExportAccounts(): Promise<LedgerExportLookup[]> {
    return http
      .get<ApiResponse<LedgerExportLookup[]>>(`generalledger/ledger-export/accounts`)
      .then(handleApiResponse);
  }

  // Dimenziókód-kijelölő lista; type = 2/3/4 -> I./II./III. dimenzió.
  static async GetLedgerExportDimCodes(type: number): Promise<LedgerExportLookup[]> {
    return http
      .get<ApiResponse<LedgerExportLookup[]>>(`generalledger/ledger-export/dim-codes/${type}`)
      .then(handleApiResponse);
  }

  // Az export futtatása — a kész TXT-t Base64-ben adja vissza (Windows-1250).
  static async ExportLedgerBalances(request: LedgerExportRequest): Promise<LedgerExportResult> {
    return http
      .post<ApiResponse<LedgerExportResult>>(`generalledger/ledger-export/export`, request)
      .then(handleApiResponse);
  }
}
